import store from '@/store'
import {
  isUK,
  isMahle,
  isGlass,
  isStGobain,
  shouldRedirectToConnect,
  getIsUserProfileComplete
} from '@/plugins/globals.js'

export default async (to, from, next) => {
  try {
    // Wait for auth init
    if (!store.getters['auth/isAuthInitialized']) {
      console.log('DEBUG => [guard] Waiting for auth init...')
      await new Promise((resolve) => {
        const unwatch = store.watch(
          (state, getters) => getters['auth/isAuthInitialized'],
          (val) => {
            if (val) {
              unwatch()
              resolve()
            }
          }
        )
      })
    }

    // Allow anonymous routes
    if (to.matched.some((record) => record.meta.allowAnonymous)) {
      return next()
    }

    let token = store.getters['auth/token']

    if (!token) {
      token = await Promise.race([
        new Promise((resolve) => {
          const unwatch = store.watch(
            (state, getters) => getters['auth/token'],
            (val) => {
              if (val) {
                unwatch()
                resolve(val)
              }
            }
          )
        }),
        new Promise((resolve, reject) => {
          setTimeout(() => reject(new Error('Token wait timeout')), 2000)
        })
      ]).catch((err) => {
        console.error(`DEBUG => [router->guard] error: ${err}`)
        return null
      })
    }

    if (!token && to.path === '/') {
      return next({ path: '/login', query: { redirect: to.fullPath } })
    }

    if (!token) {
      store.dispatch('auth/setPassthrough', to.fullPath)
      return next({ path: '/login', query: { redirect: to.fullPath } })
    }

    // LEG-9814: re-check on every navigation, not just at login, so typing a
    // URL, using the back button, or refreshing can't bypass the redirect.
    if (shouldRedirectToConnect(store.getters['auth/userProfile']?.location)) {
      return to.path === '/connect' ? next() : next({ path: '/connect' })
    }

    // Not gated — /connect is off-limits, block direct navigation to it
    if (to.path === '/connect') {
      return next({ path: '/' })
    }

    // Preload static data (Configuration values)
    const isConfigLoaded = store.getters['configuration/isConfigurationDataLoaded']
    console.log('DEBUG => [guard] isConfigLoaded:', isConfigLoaded)

    if (!isConfigLoaded) {
      console.log('DEBUG => [guard] Preloading configuration data...')
      try {
        // Step 1: Load from vuex first (this data won't change during session)
        const loadedFromStorage = await store.dispatch('configuration/loadDynamicConfigurationDataFromStorage')

        // Step 2: If no cached config found, then fetch fresh config from API (refresh)
        if (!loadedFromStorage) {
          console.log('DEBUG => [guard] No cached configuration found, fetching from API...')
          await store.dispatch('configuration/getConfigurationValues')
        }

        console.log('DEBUG => [guard] Configuration data preload complete')
      } catch (err) {
        console.error('DEBUG => [guard] Failed to preload configuration data:', err)
        await store.dispatch('auth/clearToken')
        return next('/login')
      }
    }

    // 1) Rehydrate serviceRequests lookups from storage (scantypes, serviceelements)
    await store.dispatch('serviceRequests/initLookupsFromStorage')

    // 2) If scantypes still empty, fetch with the user's location id
    const scanTypesLen = store.state.serviceRequests.scantypes?.length ?? 0
    const locId = store.getters['auth/userProfile']?.location?.id

    if (scanTypesLen === 0 && Number.isFinite(Number(locId)) && Number(locId) > 0) {
      await store.dispatch('serviceRequests/getScanTypes', {
        id: locId,
        isMahle: isMahle()
      })
    }

    // Authorization check
    if (!store.getters['auth/isAllowed'](to.path)) {
      console.log('User not allowed, redirecting Home')
      return next({ path: '/' })
    }

    // Client-specific restrictions
    if (
      isUK() &&
      to.name === 'ScanPro Assist' &&
      (!isStGobain() && !isGlass() && !isMahle())
    ) {
      const isComplete = await getIsUserProfileComplete()
      return next(isComplete ? '/' : '/user')
    }

    if (isUK() && !isMahle()) {
      const isComplete = await getIsUserProfileComplete()
      if (to.name !== 'User Profile' && (isComplete === null || isComplete === 'false')) {
        return next('/user')
      }
    }

    next()
  } catch (err) {
    console.error('[guard] Navigation error:', err)
    await store.dispatch('auth/clearToken')
    next('/login')
  }
}
